import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TaskRow as TaskRowComponent } from './TaskRow'
import { groupTasksByTeam } from './team-grouping'
import type { TaskRow, TeamGroup } from '@/types/work'

interface GroupedTaskTableProps {
  tasks: TaskRow[]
  teamGroups: TeamGroup[]
  onTaskClick: (id: number) => void
}

// US16 U6 — the grouped table variant of the list display. Renders one
// collapsible section per non-empty team (server-ordered via team_groups)
// instead of a flat paginated table. No pager: the grouped payload caps
// rows per team and sends pages:1, so navigation happens through the
// "voir N de plus" deep-link into the single-team filtered list rather
// than page numbers.
export function GroupedTaskTable({ tasks, teamGroups, onTaskClick }: GroupedTaskTableProps) {
  const sections = groupTasksByTeam(tasks, teamGroups)

  if (sections.length === 0) {
    return (
      <div className="rounded-md border border-dashed py-16 text-center">
        <p className="text-sm text-muted-foreground">Aucune tâche.</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {sections.map(({ group, tasks: groupTasks }) => (
        <TeamSection
          key={group.id}
          group={group}
          tasks={groupTasks}
          onTaskClick={onTaskClick}
        />
      ))}
    </div>
  )
}

function TeamSection({
  group,
  tasks,
  onTaskClick,
}: {
  group: TeamGroup
  tasks: TaskRow[]
  onTaskClick: (id: number) => void
}) {
  const [open, setOpen] = useState(true)
  // group.count is the TRUE per-team total (U2); tasks.length is what the
  // per-team cap (PER_TEAM_LIST_CAP) actually rendered. A positive
  // difference means the team was truncated and we surface the disclosure.
  const remaining = group.count - tasks.length

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/50"
        // depth indents nested teams so the hierarchy reads visually.
        style={{ paddingLeft: `${1 + group.depth * 1.5}rem` }}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="font-medium">{group.name}</span>
        <Badge variant="secondary">{group.count}</Badge>
      </button>

      {open && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titre</TableHead>
              <TableHead className="w-[160px]">Statut</TableHead>
              <TableHead className="w-[120px]">Priorité</TableHead>
              <TableHead className="w-[120px]">Type</TableHead>
              <TableHead className="w-[120px]">Échéance</TableHead>
              <TableHead className="w-[160px]">Assigné à</TableHead>
              <TableHead className="w-[160px]">Équipe</TableHead>
              <TableHead className="w-[160px]">Créé par</TableHead>
              <TableHead>Lié à</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((t) => (
              <TaskRowComponent
                key={t.id}
                task={t}
                canEditStatus={true}
                onTaskClick={onTaskClick}
                // Grouped sections depend on team_groups; refresh it after
                // an inline status edit so an emptied team's header/count
                // doesn't go stale (under an active status filter).
                reloadProps={['tasks', 'pagy', 'filters', 'team_groups']}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {open && remaining > 0 && (
        <div className="border-t px-4 py-2 text-sm">
          {/* Deep-link into the single-team list. group.count/remaining are
              computed from the FILTERED set (U2 counts on `filtered`), so the
              destination must carry the same active filters or the extra rows
              won't match the count the user clicked. Drop group_by (escape the
              per-team cap into the normal paginated list) and page. */}
          <a
            href={overflowHref(group.id)}
            className="text-muted-foreground hover:text-foreground hover:underline"
          >
            voir {remaining} de plus
          </a>
        </div>
      )}
    </div>
  )
}

// Build the "voir N de plus" target from the current URL so the active
// filters (status[], priority[], q, …) carry over and the destination
// list matches the count the user clicked. team_id[] is replaced with the
// clicked team; group_by and page are dropped so the user lands on the
// normal paginated single-team list rather than the capped grouped view.
function overflowHref(teamId: number): string {
  if (typeof window === 'undefined') return `/work/tasks?team_id%5B%5D=${teamId}`
  const params = new URLSearchParams(window.location.search)
  params.delete('team_id[]')
  params.delete('group_by')
  params.delete('page')
  params.append('team_id[]', String(teamId))
  return `/work/tasks?${params.toString()}`
}
